import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatGridListModule } from '@angular/material/grid-list';
import { StateService } from '../../services/state';
import detailsJson from '../../models/details.json';
import { Api } from '../../services/api';
import { HttpHeaders } from '@angular/common/http';
import { Notifications } from '../../services/notifications';
import { dateAfterTodayValidator } from '../../validators/dayAfterToday.validator';
import moment from 'moment';
import { SeguroDiasClockService } from '../../services/seguro-dias-clock';

@Component({
  selector: 'app-price-result',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCardModule,
    MatIconModule,
    MatDatepickerModule,
    MatGridListModule,
  ],
  templateUrl: './price-result.html',
  styleUrl: './price-result.scss',
})
export class PriceResult {
  priceResultForm!: FormGroup;
  today: Date = new Date();
  start_date: Date | null = null;
  end_date: Date | null = null;
  durationDays: number = 0;
  status = 0;
  stateData: any;
  private readonly formBuilder = inject(FormBuilder);
  private readonly stateService = inject(StateService);
  private readonly router = inject(Router);
  private readonly api = inject(Api);
  private readonly notification = inject(Notifications);
  private readonly clockService = inject(SeguroDiasClockService);

  public detailsData: any = detailsJson;
  public expandedDetail: string | null = null;
  public recalculateTimeout: any = null;

  // L'assistència en carretera només s'ofereix a partir d'aquest mínim de dies.
  static readonly MIN_DAYS_WITH_ASSISTANCE = 3;

  get assistanceAvailable(): boolean {
    return this.durationDays >= PriceResult.MIN_DAYS_WITH_ASSISTANCE;
  }

  get isStartInFuture(): boolean {
    return (this.start_date && this.start_date > this.today) == true;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.stateData = this.stateService.getState();

    if (!this.stateData || !this.stateData.idVersionDivina) {
      this.router.navigate(['/']);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const savedStart = this.stateData.start_date ? new Date(this.stateData.start_date) : null;
    const savedDuration = Math.min(this.stateData.duration ?? 1, 30);

    if (!savedStart || savedStart < today) {
      this.start_date = new Date(today);
      this.durationDays = savedStart ? savedDuration : 1;
    } else {
      this.start_date = savedStart;
      this.durationDays = savedDuration;
    }

    this.end_date = moment(this.start_date).add(this.durationDays, 'days').toDate();

    this.stateService.setState({
      ...this.stateData,
      start_date: this.start_date,
      end_date: this.end_date,
      duration: this.durationDays,
    });

    this.priceResultForm = this.formBuilder.group({
      datesRange: this.formBuilder.group({
        start: [this.start_date, [Validators.required, dateAfterTodayValidator]],
        end: [this.end_date, Validators.required],
      }),
    });

    this.status = 1;

    this.moveStartToServerTodayIfPast();

    this.priceResultForm.get('datesRange')!.valueChanges.subscribe((value) => {
      const start: Date | null = value.start ? moment(value.start).toDate() : null;
      const end: Date | null = value.end ? moment(value.end).toDate() : null;
      if (start && end) {
        const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        this.durationDays = duration;
      } else {
        this.durationDays = 1;
      }
      this.start_date = start;
      this.end_date = end;

      const endCtrl = this.priceResultForm.get('datesRange.end')!;
      if (this.durationDays > 30) {
        endCtrl.setErrors({ ...(endCtrl.errors || {}), maxDuration: true });
        endCtrl.markAsTouched();
      } else {
        if (endCtrl.hasError('maxDuration')) {
          const { maxDuration, ...rest } = endCtrl.errors || {};
          endCtrl.setErrors(Object.keys(rest).length ? rest : null);
        }
        if (this.durationDays > 0 && this.start_date && this.end_date)
          this.triggerRecalculation();
      }
    });
  }

  // El reajust d'ngOnInit fa servir el rellotge del navegador. Si aquest va
  // endarrerit respecte de Madrid (p.ex. Canàries entre les 23:00 i les
  // 00:00), la data d'ahir hi passaria com a vàlida i el backend la tornaria a
  // rebutjar a pay-form. Ho corregim amb el dia que dona el servidor.
  private moveStartToServerTodayIfPast() {
    this.clockService.fetch().subscribe({
      next: (clock) => {
        const startDay = this.start_date ? moment(this.start_date).format('YYYY-MM-DD') : null;
        if (!startDay || startDay >= clock.today) return;

        const start = moment(clock.today, 'YYYY-MM-DD').toDate();
        const end = moment(start).add(this.durationDays, 'days').toDate();
        // valueChanges actualitza start_date/end_date i recalcula el preu.
        this.priceResultForm.get('datesRange')!.patchValue({ start, end });
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  addDuration(days: number) {
    this.durationDays += days;
    if (this.durationDays < 1) {
      this.durationDays = 1;
    }

    this.end_date = moment(this.start_date!).add(this.durationDays, 'days').toDate();

    this.priceResultForm.patchValue({
      datesRange: {
        start: this.start_date,
        end: this.end_date,
      },
    });

    //this.triggerRecalculation();
  }

  triggerRecalculation() {
    if (this.recalculateTimeout) {
      clearTimeout(this.recalculateTimeout);
    }

    this.recalculateTimeout = setTimeout(() => {
      this.recalculateTimeout = null;

      this.stateService.setState({
        ...this.stateData,
        start_date: this.start_date,
        end_date: this.end_date,
        duration: this.durationDays,
      });

      this.api
        .post(
          '/seguro-dias',
          {
            campaignId: this.stateData?.campaignId ?? null,
            collaboratorId: this.stateData?.collaboratorId ?? null,
            plate: this.stateData.licensePlate,
            brand: this.stateData.brandName,
            idBrandDivina: this.stateData.idBrandDivina,
            model: this.stateData.modelName,
            idModelDivina: this.stateData.idModelDivina,
            version: this.stateData.versionName,
            idVersionDivina: this.stateData.idVersionDivina,
            type: this.stateData?.vehicleType,
            postalCode: this.stateData.postalCode,
            birthday: this.stateData.birthday,
            tkn: this.stateData?.tkn,
            days: this.durationDays,
          },
          {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
          },
        )
        .subscribe(
          (data: any) => {
            this.stateService.setState(Object.assign(this.stateService.getState(), { ...data }));
            this.stateData = Object.assign({}, this.stateService.getState());
            this.cdr.markForCheck();
          },
          (error) => {
            //TODO notify error
          },
        );
    }, 1000);
  }

  continue(withAssistance: boolean) {
    if (withAssistance && !this.assistanceAvailable) return;

    this.start_date = this.priceResultForm.value.datesRange.start;
    this.end_date = this.priceResultForm.value.datesRange.end;

    const duration = this.durationDays
      ? this.durationDays
      : this.start_date && this.end_date
        ? Math.ceil(
            (new Date(this.end_date).getTime() - new Date(this.start_date).getTime()) /
              (1000 * 60 * 60 * 24),
          )
        : 1;

    this.stateService.setState({
      ...this.stateData,
      withAssistance,
      start_date: this.start_date,
      end_date: this.end_date,
      duration: duration,
    });

    if (this.priceResultForm.invalid) {
      this.notification.showError('El rango de fechas no es válido');
    } else {
      this.router.navigate(['/product-form']);
    }
    //    this.router.navigate(['/next-page']);
  }

  onSubmit() {}
}
